import { useEffect, useState } from "react";
import { Icon } from "../components/Icon.js";
import { Avatar } from "../components/Avatar.js";
import { HowToApprove } from "../components/HowToApprove.js";
import {
  approveConnectorRequest,
  listConnectorRequests,
  rejectConnectorRequest,
  type ConnectorRequest,
} from "../api/client.js";

export function ApprovalsPage() {
  const [requests, setRequests] = useState<ConnectorRequest[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [approveModal, setApproveModal] = useState<ConnectorRequest | null>(null);
  const [rejectModal, setRejectModal] = useState<ConnectorRequest | null>(null);

  async function refresh() {
    setError(null);
    try {
      const r = await listConnectorRequests();
      setRequests(r.requests);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't load requests.");
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  const pending = requests?.filter((r) => r.status === "awaiting_approval") ?? [];
  const decided = requests?.filter((r) => r.status !== "awaiting_approval") ?? [];

  return (
    <div className="flex flex-col gap-lg">
      <div>
        <h1 className="text-headline-md text-on-background">Connector approvals</h1>
        <p className="text-body-sm text-on-surface-variant mt-xs">
          When a colleague requests a Gmail connection, the email must first be added to Google Cloud's Test Users
          list. Approving here records that you've done it.
        </p>
      </div>

      {error && (
        <div className="bg-error-container/20 border border-error/30 text-error rounded-lg p-3 text-body-sm flex items-center gap-2">
          <Icon name="error" /> {error}
        </div>
      )}

      <HowToApprove />

      <Section title="Pending approval" count={pending.length}>
        {requests === null ? (
          <Skeleton rows={2} />
        ) : pending.length === 0 ? (
          <Empty icon="check_circle" message="No pending requests. You're all caught up." />
        ) : (
          <div className="divide-y divide-outline-variant/30">
            {pending.map((r) => (
              <PendingRow
                key={r.id}
                request={r}
                onApprove={() => setApproveModal(r)}
                onReject={() => setRejectModal(r)}
              />
            ))}
          </div>
        )}
      </Section>

      <Section title="Decided" count={decided.length}>
        {decided.length === 0 ? (
          <Empty icon="history" message="No decided requests yet." />
        ) : (
          <div className="divide-y divide-outline-variant/30">
            {decided.map((r) => (
              <DecidedRow key={r.id} request={r} />
            ))}
          </div>
        )}
      </Section>

      {approveModal && (
        <ApproveModal
          request={approveModal}
          onClose={() => setApproveModal(null)}
          onApproved={() => {
            setApproveModal(null);
            void refresh();
          }}
        />
      )}
      {rejectModal && (
        <RejectModal
          request={rejectModal}
          onClose={() => setRejectModal(null)}
          onRejected={() => {
            setRejectModal(null);
            void refresh();
          }}
        />
      )}
    </div>
  );
}

function PendingRow({
  request,
  onApprove,
  onReject,
}: {
  request: ConnectorRequest;
  onApprove: () => void;
  onReject: () => void;
}) {
  return (
    <div className="px-md py-md flex flex-col md:flex-row md:items-center gap-md">
      <div className="flex items-center gap-md flex-1 min-w-0">
        <Avatar name={request.user.name ?? request.user.email} tone="primary" />
        <div className="min-w-0">
          <p className="text-body-base text-on-surface font-semibold truncate">
            {request.user.name ?? request.user.email}
          </p>
          <p className="text-label-xs font-mono text-on-surface-variant truncate">{request.user.email}</p>
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-label-xs font-mono text-on-surface-variant uppercase">Wants to connect</p>
        <p className="text-body-base text-on-surface font-mono truncate">{request.email}</p>
        {request.requestedAt && (
          <p className="text-[10px] font-mono text-outline mt-0.5">
            Requested {new Date(request.requestedAt).toLocaleString()}
          </p>
        )}
      </div>
      <div className="flex gap-2 shrink-0">
        <button
          onClick={onReject}
          className="px-3 py-1.5 border border-outline-variant rounded text-label-xs font-mono uppercase text-on-surface-variant hover:bg-surface-variant transition-colors"
        >
          Reject
        </button>
        <button
          onClick={onApprove}
          className="px-3 py-1.5 bg-primary-container text-on-primary-container rounded text-label-xs font-mono uppercase tracking-widest flex items-center gap-1 active:scale-[0.98]"
        >
          <Icon name="check" className="text-[14px]" />
          Approve
        </button>
      </div>
    </div>
  );
}

function DecidedRow({ request }: { request: ConnectorRequest }) {
  const tone =
    request.status === "approved"
      ? "text-primary-fixed-dim"
      : request.status === "active"
        ? "text-primary-fixed-dim"
        : request.status === "rejected"
          ? "text-error"
          : "text-on-surface-variant";
  return (
    <div className="px-md py-md flex items-center gap-md">
      <Avatar name={request.user.name ?? request.user.email} tone="muted" size="sm" />
      <div className="flex-1 min-w-0">
        <p className="text-body-sm text-on-surface truncate">
          <span className="font-semibold">{request.user.email}</span>{" "}
          <span className="text-on-surface-variant">→</span>{" "}
          <span className="font-mono">{request.email}</span>
        </p>
        <p className="text-[10px] font-mono text-outline mt-0.5">
          {request.status === "rejected" && request.adminNotes ? `Reason: ${request.adminNotes}` : ""}
          {request.approvedAt && ` · ${new Date(request.approvedAt).toLocaleString()}`}
        </p>
      </div>
      <span className={`text-label-xs font-mono uppercase ${tone}`}>{request.status}</span>
    </div>
  );
}

function ApproveModal({
  request,
  onClose,
  onApproved,
}: {
  request: ConnectorRequest;
  onClose: () => void;
  onApproved: () => void;
}) {
  const [step, setStep] = useState<1 | 2>(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function confirmApprove() {
    setSubmitting(true);
    setError(null);
    try {
      await approveConnectorRequest(request.id);
      onApproved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Approve failed.");
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[100] bg-background/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="glass-card-elevated w-full max-w-2xl rounded-xl p-6 space-y-4 max-h-[90vh] overflow-y-auto scrollbar-custom"
      >
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-headline-md text-on-surface">Approve {request.email}</h2>
            <p className="text-body-sm text-on-surface-variant mt-1">
              For <span className="font-mono">{request.user.email}</span>
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-on-surface-variant hover:text-on-surface">
            <Icon name="close" />
          </button>
        </div>

        {step === 1 && (
          <>
            <HowToApprove highlightEmail={request.email ?? ""} variant="modal" />

            {error && (
              <div className="bg-error-container/20 border border-error/30 text-error rounded-lg p-3 text-body-sm flex items-center gap-2">
                <Icon name="error" /> {error}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={onClose}
                className="px-4 py-2 text-label-xs font-mono uppercase text-on-surface-variant hover:text-on-surface"
              >
                Cancel
              </button>
              <button
                onClick={() => setStep(2)}
                className="px-6 py-2 bg-primary-container text-on-primary-container rounded-lg text-label-xs font-mono uppercase tracking-widest flex items-center gap-1"
              >
                I've added it
                <Icon name="arrow_forward" className="text-[14px]" />
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="bg-primary-container/10 border border-primary-container/30 rounded p-md text-body-sm text-on-surface">
              <p className="font-semibold flex items-center gap-2 text-primary-fixed-dim">
                <Icon name="check_circle" /> Final confirmation
              </p>
              <p className="mt-2 text-on-surface-variant">
                You confirm that <span className="font-mono text-on-surface">{request.email}</span> has been added to
                Google Cloud's Test Users list. Approving here unlocks the Connect button for{" "}
                <span className="font-mono text-on-surface">{request.user.email}</span>.
              </p>
            </div>

            {error && (
              <div className="bg-error-container/20 border border-error/30 text-error rounded-lg p-3 text-body-sm flex items-center gap-2">
                <Icon name="error" /> {error}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setStep(1)}
                className="px-4 py-2 text-label-xs font-mono uppercase text-on-surface-variant hover:text-on-surface"
              >
                Back
              </button>
              <button
                onClick={confirmApprove}
                disabled={submitting}
                className="px-6 py-2 bg-primary-container text-on-primary-container rounded-lg text-label-xs font-mono uppercase tracking-widest disabled:opacity-60"
              >
                {submitting ? "Approving…" : "Confirm approval"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function RejectModal({
  request,
  onClose,
  onRejected,
}: {
  request: ConnectorRequest;
  onClose: () => void;
  onRejected: () => void;
}) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function confirmReject() {
    setSubmitting(true);
    setError(null);
    try {
      await rejectConnectorRequest(request.id, reason.trim() || undefined);
      onRejected();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Reject failed.");
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[100] bg-background/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="glass-card-elevated w-full max-w-md rounded-xl p-6 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-headline-md text-on-surface">Reject this request</h2>
            <p className="text-body-sm text-on-surface-variant mt-1">
              <span className="font-mono">{request.email}</span> for{" "}
              <span className="font-mono">{request.user.email}</span>
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-on-surface-variant hover:text-on-surface">
            <Icon name="close" />
          </button>
        </div>
        <div className="space-y-1.5">
          <label htmlFor="reject-reason" className="block text-label-xs font-mono text-outline uppercase tracking-widest">
            Reason (optional, shown to the colleague)
          </label>
          <textarea
            id="reject-reason"
            placeholder="e.g. wrong domain, please request from your work account"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            className="w-full bg-surface-container-lowest border border-outline-variant rounded-lg px-3 py-2 text-on-surface text-body-sm placeholder:text-outline/50 focus:outline-none focus:border-primary-container resize-y"
          />
        </div>
        {error && (
          <div className="bg-error-container/20 border border-error/30 text-error rounded-lg p-3 text-body-sm flex items-center gap-2">
            <Icon name="error" /> {error}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-label-xs font-mono uppercase text-on-surface-variant hover:text-on-surface">
            Cancel
          </button>
          <button
            onClick={confirmReject}
            disabled={submitting}
            className="px-6 py-2 bg-error text-on-error rounded-lg text-label-xs font-mono uppercase tracking-widest disabled:opacity-60"
          >
            {submitting ? "Rejecting…" : "Reject request"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({
  title,
  count,
  children,
}: {
  title: string;
  count?: number;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-surface-container border border-outline-variant rounded-xl overflow-hidden shadow-xl shadow-black/20">
      <div className="px-md py-md border-b border-outline-variant flex items-center gap-3">
        <h2 className="text-headline-md text-on-surface">{title}</h2>
        {typeof count === "number" && (
          <span className="text-label-xs font-mono text-on-surface-variant uppercase">{count}</span>
        )}
      </div>
      {children}
    </div>
  );
}

function Empty({ icon, message }: { icon: string; message: string }) {
  return (
    <div className="text-center py-12 text-on-surface-variant">
      <Icon name={icon} className="text-primary-fixed-dim/40" style={{ fontSize: "48px" }} />
      <p className="mt-3 text-body-sm">{message}</p>
    </div>
  );
}

function Skeleton({ rows }: { rows: number }) {
  return (
    <div className="p-md space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-16 bg-surface-container-low rounded animate-pulse" />
      ))}
    </div>
  );
}
